import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, FileText, Loader2, Search, X } from 'lucide-react';
import { safeFetch } from '../../lib/api';

/**
 * DocumentTemplateMultiSelect — shared picker for "documents to generate
 * and attach when this email is sent".
 *
 * Lists the workspace's PUBLISHED document templates (the same population
 * the applicant Generated Documents picker offers), multi-select with
 * removable chips. The selection is a list of document template ids
 * (`documentTemplateIds`); generation itself happens server-side at send
 * time, per recipient, from that recipient's application.
 *
 * The option list expands IN FLOW (never an absolutely-positioned popover),
 * so it cannot be clipped by modal overflow containers on any viewport.
 * Selected ids that are no longer in the published list (e.g. a template
 * archived after being attached to an email template) still render as
 * removable chips — a stored selection is never silently dropped.
 */

export interface DocumentTemplateOption {
  id: string;
  name: string;
  code?: string;
}

/** Map a raw /api/admin/document-templates payload to picker options (published only, name-sorted). */
export function publishedDocumentTemplateOptions(templates: any[]): DocumentTemplateOption[] {
  return (Array.isArray(templates) ? templates : [])
    .filter((t: any) => (t?.status || 'draft') === 'published')
    .map((t: any) => ({
      id: String(t.id || t.templateId || ''),
      name: String(t.name || ''),
      code: t.code ? String(t.code) : undefined,
    }))
    .filter((t: DocumentTemplateOption) => t.id)
    .sort((a: DocumentTemplateOption, b: DocumentTemplateOption) => a.name.localeCompare(b.name));
}

interface DocumentTemplateMultiSelectProps {
  /** Selected document template ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Section label (rendered with the selection count). */
  label?: string;
  /** Helper line under the label. */
  hint?: string;
  disabled?: boolean;
}

export default function DocumentTemplateMultiSelect({
  value,
  onChange,
  label = 'Document Templates',
  hint = 'Generated from each recipient’s application and attached when the email is sent.',
  disabled = false,
}: DocumentTemplateMultiSelectProps) {
  const [options, setOptions] = useState<DocumentTemplateOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => `${o.name} ${o.code || ''}`.toLowerCase().includes(q));
  }, [options, query]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await safeFetch('/api/admin/document-templates');
      setOptions(publishedDocumentTemplateOptions(data?.templates || []));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.id, o.code ? `[${o.code}] ${o.name}` : o.name);
    return map;
  }, [options]);

  const toggle = (id: string) => {
    if (disabled) return;
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  const remove = (id: string) => {
    if (disabled) return;
    onChange(value.filter(v => v !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <FileText className="h-4 w-4 text-indigo-600" />
          <span className="text-caption uppercase font-extrabold text-slate-600">{label} ({value.length})</span>
        </div>
      </div>
      {hint && <p className="text-caption text-slate-400 mb-2">{hint}</p>}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map(id => (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-indigo-50 border border-indigo-100 rounded-lg text-xs font-bold text-indigo-900 max-w-full"
            >
              <span className="truncate max-w-[200px]">{nameById.get(id) || id}</span>
              <button
                type="button"
                aria-label={`Remove ${nameById.get(id) || id}`}
                onClick={() => remove(id)}
                disabled={disabled}
                className="text-indigo-400 hover:text-rose-600 cursor-pointer p-0.5 shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled || loading}
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 hover:border-indigo-300 rounded-xl text-xs font-bold text-slate-700 transition-all cursor-pointer disabled:opacity-60 disabled:cursor-default"
      >
        <span className="flex items-center gap-2 min-w-0">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500 shrink-0" /> : <FileText className="h-3.5 w-3.5 text-indigo-500 shrink-0" />}
          <span className="truncate">
            {loading
              ? 'Loading document templates…'
              : loadError
                ? 'Couldn’t load document templates'
                : options.length === 0
                  ? 'No published document templates'
                  : value.length === 0
                    ? 'Select document templates…'
                    : `${value.length} document template${value.length === 1 ? '' : 's'} selected`}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {loadError && !loading && (
        <button type="button" onClick={load} className="mt-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer">
          Retry loading templates
        </button>
      )}

      {open && !loading && !loadError && (
        <div role="listbox" aria-multiselectable="true" aria-label={label} className="mt-2 border border-slate-200 rounded-xl overflow-hidden">
          {options.length === 0 ? (
            <p className="px-3 py-2.5 text-caption text-slate-400 italic">
              No published document templates in this workspace yet. Publish a document template to attach generated documents.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 border-b border-slate-100">
                <Search className="h-3.5 w-3.5 text-slate-400 shrink-0 pointer-events-none" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search document templates…"
                  aria-label="Search document templates"
                  className="flex-1 min-w-0 py-2 text-xs outline-none placeholder:text-slate-400"
                />
              </div>
              {visibleOptions.length === 0 ? (
                <p className="px-3 py-2.5 text-caption text-slate-400 italic">No document templates match your search.</p>
              ) : (
            <div className="max-h-60 overflow-y-auto divide-y divide-slate-100">
              {visibleOptions.map(option => {
                const selected = value.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => toggle(option.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-bold transition-colors cursor-pointer ${selected ? 'bg-indigo-50/70 text-indigo-900' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    <span className={`flex items-center justify-center h-4 w-4 rounded border shrink-0 ${selected ? 'bg-brand-primary border-brand-primary text-white' : 'border-slate-300 text-transparent'}`}>
                      <Check className="h-3 w-3" />
                    </span>
                    <span className="min-w-0 break-words">{option.code ? `[${option.code}] ` : ''}{option.name || option.id}</span>
                  </button>
                );
              })}
            </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
